"use client"

import { useState, useEffect } from "react"
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Users,
  Award,
  Download,
  Upload,
  Plus,
  BarChart,
  Settings,
  Eye,
  EyeOff,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  Banknote,
  Network,
  Gift,
  Star,
  Activity,
  Target,
  DollarSign,
  CreditCard,
  PieChart,
  ChevronRight,
  Bell,
  Search,
  Filter,
  Calendar,
  Shield,
  Zap,
  Wallet2Icon,
  CheckCheck,
  Loader2
} from "lucide-react"
import WrapUpP2P from './layouts/WrapUpP2p'
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { allRemoteSettingsSelect, exchangeSelect, homeScreenStatSelect, userSelect } from "../../../redux/selectors"
import { formatDate, notify, money } from "../../../utility/Helper1"
import { authActions, peerActions } from "../../../redux/actions"


const P2PHomepage = () => {
  // Mock Redux data for demo
  const navigate = useNavigate();
  const userData = useSelector(userSelect);
  const site_settings = useSelector(allRemoteSettingsSelect)
  const finance = useSelector(homeScreenStatSelect)
  const exchangeData = useSelector(exchangeSelect);

  const dispatch = useDispatch();
  const [isBalanceVisible, setIsBalanceVisible] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState(0)
  const [refDataRaw, setRefDataRaw] = useState([])
  const [marketLoading, setMarketLoading] = useState(false)
  // Countdown states for activate bid button
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    total: 0,
  });
  const [isMarketExpired, setIsMarketExpired] = useState(false);
  const [isMarketClosed, setIsMarketClosed] = useState(false);

  // Calculate stats from exchange data
  const allWithdrawals = exchangeData?.filter(el => el.asset == 2) || []
  const pendingWithdrawals = allWithdrawals.filter(item => item.completed == 0).length
  const completedWithdrawals = allWithdrawals.filter(item => item.completed == 1).length
  const totalWithdrawn = allWithdrawals.reduce((sum, item) => sum + Number.parseFloat(item.readOnlyTotalWithdrawn || 0), 0)
  const allInvestments = exchangeData?.filter(el => el.asset == 1) || []

  const currentFinance = finance?.[selectedCurrency] || finance?.[0]
  const mainBalance = currentFinance?.balance || 0
  const commission = currentFinance?.commission || 0
  const totalBalance = Number(mainBalance) + Number(commission)

  const loadDownLines = (myrefid) => {
    dispatch(authActions.onRefreshSystemData(`user/referral/${myrefid}`, 10, {}, (response) => {
      setRefDataRaw(response?.data)
    }),)
  }

  const totalRefGrowth = Number(refDataRaw?.referrals?.level1?.length ?? 0) + Number(refDataRaw?.referrals?.level2?.length ?? 0) + Number(refDataRaw?.referrals?.level3?.length ?? 0)

  useEffect(() => {
    loadDownLines(userData?.myrefid)
  }, [])

  // Countdown logic for activate bid button
  useEffect(() => {
    const settings = site_settings?.operational_settings;

    if (!settings?.p2p_markettimeStart || !settings?.p2p_markettimeEnds) return;

    const targetDate = new Date(settings.p2p_markettimeStart);
    const closingTime = new Date(settings.p2p_markettimeEnds);
    const calculateTimeRemaining = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();
      const closingDifference = closingTime.getTime() - now.getTime();

      // Check if market has closed
      if (closingDifference <= 0) {
        setIsMarketClosed(true);
      }

      if (difference <= 0) {
        setIsMarketExpired(true);
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          total: 0
        };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        total: difference,
      };
    };

    setTimeRemaining(calculateTimeRemaining());

    const timerInterval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      if (remaining.total <= 0) {
        clearInterval(timerInterval);
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [site_settings]);

  const formatDigit = (value) => value < 10 ? `0${value}` : value;

  const getActivateBidButtonContent = () => {
    if (isMarketClosed) {
      return "Market Closed";
    }

    if (isMarketExpired) {
      return "ACTIVATE BID";
    }

    if (timeRemaining.total > 0) {
      return `${formatDigit(timeRemaining.days)}d ${formatDigit(timeRemaining.hours)}h ${formatDigit(timeRemaining.minutes)}m ${formatDigit(timeRemaining.seconds)}s`;
    }

    return "ACTIVATE BID";
  };

  const isActivateBidClickable = () => {
    return isMarketExpired && !isMarketClosed;
  };

  const getElapsedPercentage = (phpTimestamp, days) => {
    if (!phpTimestamp || !days) return 0;
    const start = phpTimestamp * 1000;
    const end = start + (days * 24 * 60 * 60 * 1000);
    const now = Date.now();
    if (now >= end) return 100;
    const elapsed = now - start;
    const total = end - start;
    return Math.min(Math.max((elapsed / total) * 100, 0), 100);
  }

  const getInvestmentStatus = (investment) => {
    if (!investment.timephconf2 || investment.timephconf2 == 0) {
      return { status: 'pending', label: 'Processing', color: 'amber' }
    }

    if (investment.matchedph == 0) {
      return { status: 'unmatched', label: 'Unmatched', color: 'blue' }
    }

    const progress = getElapsedPercentage(investment.timephconf2, investment.ghday)

    if (progress >= 100 || investment.phready == "NO") {
      return { status: 'matured', label: 'Matured', color: 'green' }
    }

    if (investment.matchedph > 0) {
      return { status: 'active', label: 'Active', color: 'emerald' }
    }

    return { status: 'pending', label: 'Processing', color: 'amber' }
  }

  const getTimeAgo = (timestamp) => {
    const now = Date.now()
    const diffInSeconds = Math.floor((now - (timestamp * 1000)) / 1000)

    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    return `${Math.floor(diffInSeconds / 86400)} days ago`
  }


  // Get first 10 of each type
  const first10Investments = allInvestments.slice(0, 10)
  const first10Withdrawals = allWithdrawals.slice(0, 10)

  // Investment stats
  const activeInvestments = allInvestments.filter(inv => {
    return inv.phready == 'YES'
  }).length

  const pendingMaturity = allInvestments.filter(inv => {
    return inv.phready == 'NO' && inv.locked == 0
  }).length

  const maturedInvestments = allInvestments.filter(inv => {
    return inv.phready == 'NO' && inv.locked == 1
  }).length


  // Create combined transaction history from investments and withdrawals
  const createTransactionHistory = () => {
    const transactions = []

    // Add investment transactions
    first10Investments.forEach(inv => {
      const status = getInvestmentStatus(inv)
      transactions.push({
        ...inv,
        id: inv.id,
        type: "investment",
        title: status.status == 'matured' ? "Investment Matured" : "Investment Created",
        description: inv.transid,
        amount: status.status == 'matured' ? inv.ph : -inv.lastph,
        time: getTimeAgo(inv.phtimestamp2),
        status: status.status,
        statusLabel: status.label,
        statusColor: status.color,
        icon: status.status == 'matured' ? <CheckCircle className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />,
        timestamp: inv.phtimestamp2
      })
    })

    // Add withdrawal transactions
    first10Withdrawals.forEach(wth => {
      transactions.push({
        ...wth,
        id: wth.id,
        type: "withdrawal",
        title: wth.completed == 1 ? "Withdrawal Completed" : "Withdrawal Processing",
        description: wth.transid,
        amount: wth.completed == 1 ? wth.readOnlyTotalWithdrawn : -wth.readOnlyTotalWithdrawn,
        time: getTimeAgo(wth.ghtimestamp2),
        status: wth.completed == 1 ? "completed" : "pending",
        statusLabel: wth.completed == 1 ? "Completed" : "Processing",
        statusColor: wth.completed == 1 ? "green" : "amber",
        icon: wth.completed == 1 ? <Download className="h-4 w-4" /> : <Clock className="h-4 w-4" />,
        timestamp: wth.phtimestamp2
      })
    })

    // Sort by timestamp (most recent first)
    return transactions.sort((a, b) => b.timestamp - a.timestamp).slice(0, 5)
  }

  const recentTransactions = createTransactionHistory()

  const getStatusColor = (color) => {
    const colors = {
      green: 'bg-green-500/20 border-green-500/30 text-green-400',
      emerald: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400',
      amber: 'bg-amber-500/20 border-amber-500/30 text-amber-400',
      blue: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
      red: 'bg-red-500/20 border-red-500/30 text-red-400',
      gray: 'bg-gray-500/20 border-gray-500/30 text-gray-400'
    }
    return colors[color] || colors.gray
  }


  // Premium quick actions
  const quickActions = [
    {
      title: "Create Investment",
      description: "Launch new P2P opportunity",
      icon: <Plus className="h-5 w-5" />,
      link: "/p2p/create-bid",
      category: "Primary"
    },
    {
      title: "Market Analysis",
      description: "View market insights",
      icon: <BarChart className="h-5 w-5" />,
      link: "/p2p/analytics",
      category: "Analysis"
    },
    {
      title: "Portfolio Review",
      description: "Comprehensive overview",
      icon: <Target className="h-5 w-5" />,
      link: "/p2p/portfolio",
      category: "Review"
    },
    {
      title: "Funds Management",
      description: "Deposits & withdrawals",
      icon: <Wallet className="h-5 w-5" />,
      link: "/wallet",
      category: "Finance"
    }
  ]

  // Premium stats
  const performanceMetrics = [
    {
      title: "Commission Revenue",
      value: money(commission, currentFinance?.type),
      change: { value: 8.9, period: "YTD" },
      trend: "up",
      icon: <Award className="h-5 w-5" />
    },
    // {
    //   title: "Total Assets",
    //   value: money(totalBalance, currentFinance?.type),
    //   change: { value: 15.7, period: "YTD" },
    //   trend: "up",
    //   icon: <Wallet className="h-5 w-5" />
    // },
    // {
    //   title: "Active Yield",
    //   value: "12.8%",
    //   change: { value: 2.3, period: "YTD" },
    //   trend: "up",
    //   icon: <TrendingUp className="h-5 w-5" />
    // },
    {
      title: "Network Growth",
      value: totalRefGrowth,
      change: { value: 12, period: "YTD" },
      trend: "up",
      icon: <Users className="h-5 w-5" />
    }
  ]


  const handleGoToMarket = () => { //async

    const allAccountPH = exchangeData?.reduce((sum, item) => sum + (Number(item.ph) || 0), 0);
    if (allAccountPH <= 0) {
      notify('You must make a BID first.', 0)
      return;
    }
    if (marketLoading) {
      return;
    }

    setMarketLoading(true)


    dispatch(peerActions.onPost({ loadMarket: true }, `p2p/market/time`, response => {

      if (response?.code == 1) {
        navigate('/peerings')
        notify('Follow the payment instructions below to activate your bid.', 1)
      } else {
        notify('Bid not completed. Please try again.', 0)
      }
      setMarketLoading(false)
    }),
    )

  }

  return (<WrapUpP2P>

    {/* Premium Portfolio Overview */}
    <div className="mb-8">
      <div className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-slate-700/50 overflow-hidden">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-transparent to-yellow-500/10"></div>
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent"></div>
        </div>

        <div className="relative p-8">
          <div className="flex items-start justify-between mb-8">
            <div className="space-y-1">
              <p className="text-slate-400 text-sm font-light tracking-wide uppercase">Portfolio Value</p>
              <div className="flex items-center space-x-4">
                <h2 className="text-4xl font-light text-white tracking-tight">
                  {money(totalBalance, currentFinance?.type)}
                </h2>
              </div>
              <div className="flex items-center space-x-3 mt-3">
                <span className="text-slate-400 text-sm">Wallet + Commission</span>
              </div>
            </div>

            <div style={{ cursor: 'pointer' }} className="text-right space-y-2">
              <div onClick={() => navigate('/new/bid')} className="px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
                <div className="flex items-center justify-center gap-1.5">
                  <Wallet2Icon className="w-3 h-3 text-emerald-300" />
                  <span className="text-emerald-300 text-xs font-medium tracking-wide">MAKE A BID</span>
                </div>
              </div>
              <div
                onClick={isActivateBidClickable() && !marketLoading ? handleGoToMarket : undefined}
                className={`px-3 py-1.5 rounded-full border transition-all duration-200 ${marketLoading
                    ? 'bg-blue-500/20 border-blue-500/30 cursor-not-allowed'
                    : isActivateBidClickable()
                      ? 'bg-amber-500/20 border-amber-500/30 cursor-pointer hover:bg-amber-500/30'
                      : isMarketClosed
                        ? 'bg-red-500/20 border-red-500/30 cursor-not-allowed'
                        : 'bg-gray-500/20 border-gray-500/30 cursor-not-allowed'
                  }`}
              >
                <div className="flex items-center justify-center gap-1.5">
                  {marketLoading ? (
                    <Loader2 className="w-3 h-3 text-blue-300 animate-spin" />
                  ) : isMarketClosed ? (
                    <XCircle className="w-3 h-3 text-red-300" />
                  ) : isActivateBidClickable() ? (
                    <CheckCheck className="w-3 h-3 text-amber-300" />
                  ) : (
                    <Clock className="w-3 h-3 text-gray-300" />
                  )}
                  <span className={`text-xs font-medium tracking-wide ${marketLoading
                      ? 'text-blue-300'
                      : isActivateBidClickable()
                        ? 'text-amber-300'
                        : isMarketClosed
                          ? 'text-red-300'
                          : 'text-gray-300'
                    }`}>
                    {marketLoading ? 'Activating...' : getActivateBidButtonContent()}
                  </span>
                </div>
              </div>

            </div>
          </div>

          {/* Elegant Balance Breakdown */}
          <div className="grid grid-cols-1 gap-6">

            <div className="group bg-slate-800/30 backdrop-blur-sm rounded-xl p-5 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-light mb-1">Total Wallet</p>
                  <p className="text-2xl font-light text-white">{money(mainBalance, currentFinance?.type)}</p>
                </div>
                <div className="p-3 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl border border-blue-500/30">
                  <Wallet className="h-5 w-5 text-blue-400" />
                </div>
              </div>
            </div>

            {/* <div className="group bg-slate-800/30 backdrop-blur-sm rounded-xl p-5 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-light mb-1">Commission Earned</p>
                  <p className="text-2xl font-light text-white">{formatBalance(commission)}</p>
                </div>
                <div className="p-3 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-xl border border-amber-500/30">
                  <Award className="h-5 w-5 text-amber-400" />
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>

    {/* Refined Performance Metrics */}
    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
      {performanceMetrics.map((metric, index) => (
        <div
          key={index}
          className="group bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-amber-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

          <div className="flex items-start justify-between mb-4">
            <div className="p-2.5 bg-slate-700/50 rounded-lg border border-slate-600/50">
              {metric.icon}
            </div>
            {/* <div className="text-right">
              <div className="flex items-center text-xs text-emerald-400 font-medium">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                +{metric.change.value}%
              </div>
              <p className="text-slate-500 text-xs mt-0.5">{metric.change.period}</p>
            </div> */}
          </div>

          <div>
            <h3 className="text-slate-400 text-sm font-light mb-2">{metric.title}</h3>
            <p className="text-xl font-light text-white">{metric.value}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Premium Quick Actions */}
    <div className="mb-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-light text-white flex items-center">
          <Zap className="h-5 w-5 mr-3 text-amber-400" />
          Quick Overview
        </h2>
      </div>
    </div>

    {/* Dashboard Grid */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Transaction History */}
      <div className="lg:col-span-2">
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-700/50">
          <div className="p-6 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-light text-white flex items-center">
                <Activity className="h-5 w-5 mr-3 text-amber-400" />
                Recent Transactions
              </h2>
              <div className="flex items-center space-x-3">
                <button className="p-2 rounded-lg bg-slate-700/50 border border-slate-600/50 hover:bg-slate-600/50 transition-all duration-200">
                  <Filter className="h-4 w-4 text-slate-400" />
                </button>
                <button onClick={() => navigate('/all/deposit/withdrawal')} className="text-amber-400 hover:text-amber-300 text-sm font-light flex items-center transition-colors duration-200">
                  View All
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>

          {recentTransactions.length > 0 ? <div className="p-6">
            <div className="space-y-4">
              {recentTransactions.map((transaction, index) => {

                let capital = transaction?.asset == 1 ? transaction.lastph : transaction.lastgh

                return <div
                  key={`${transaction.type}-${transaction.id}`}
                  className="group flex items-center justify-between p-4 bg-slate-700/20 rounded-lg border border-slate-600/30 hover:border-slate-500/50 transition-all duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-2.5 rounded-lg border ${getStatusColor(transaction.statusColor)}`}>
                      {transaction.icon}
                    </div>
                    <div>
                      <h4 className="text-white font-medium text-sm">{transaction.title}</h4>
                      <p className="text-slate-400 text-xs font-light mt-0.5">{transaction.description}</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full border ${getStatusColor(transaction.statusColor)}`}>
                        {transaction.statusLabel}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-medium text-sm ${transaction.amount > 0 ? 'text-emerald-400' : 'text-slate-300'}`}>
                      {transaction.amount > 0 ? '+' : ''}{money(capital, currentFinance?.type)}
                    </p>
                    <p className="text-slate-500 text-xs mt-0.5">{transaction.time}</p>
                  </div>
                </div>

              })}
            </div>
          </div>:<div className="flex flex-col items-center justify-center py-12">
            <div className="p-4 rounded-full bg-slate-700/30 border border-slate-600/30 mb-4">
              <Activity className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-slate-300 font-medium text-base mb-2">No Recent Transactions</h3>
            <p className="text-slate-500 text-sm text-center max-w-xs">
              Your transaction history will appear here once you start making investments and withdrawals.
            </p>
            <button
              onClick={() => navigate('/new/bid')}
              className="mt-4 px-4 py-2 bg-amber-500/20 border border-amber-500/30 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-all duration-200 text-sm font-medium"
            >
              Start Investing
            </button>
          </div>}
        </div>
      </div>

      {/* Sidebar Widgets */}
      <div className="space-y-6">
        {/* Investment Status */}
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-700/50">
          <div className="p-5 border-b border-slate-700/50">
            <h3 className="text-base font-light text-white flex items-center">
              <PieChart className="h-4 w-4 mr-2 text-amber-400" />
              Bid Status
            </h3>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-emerald-400 rounded-full"></div>
                <span className="text-slate-300 text-sm font-light">Active Bid</span>
              </div>
              <span className="text-white font-medium">{activeInvestments}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-amber-400 rounded-full"></div>
                <span className="text-slate-300 text-sm font-light">Processing Maturity</span>
              </div>
              <span className="text-white font-medium">{pendingMaturity}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                <span className="text-slate-300 text-sm font-light">Matured</span>
              </div>
              <span className="text-white font-medium">{maturedInvestments}</span>
            </div>
          </div>
        </div>

        {/* Withdrawal Management */}
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-700/50">
          <div className="p-5 border-b border-slate-700/50">
            <h3 className="text-base font-light text-white flex items-center">
              <Download className="h-4 w-4 mr-2 text-amber-400" />
              Withdrawal Center
            </h3>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Clock className="h-4 w-4 text-amber-400" />
                <span className="text-slate-300 text-sm font-light">Processing</span>
              </div>
              <span className="text-amber-400 font-medium">{pendingWithdrawals}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                <span className="text-slate-300 text-sm font-light">Completed</span>
              </div>
              <span className="text-emerald-400 font-medium">{completedWithdrawals}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

  </WrapUpP2P>
  )
}

export default P2PHomepage